import {
  EmptyState,
  FlexBox,
  PageWithActions,
} from '@airship-platform/jam-web/components';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Fragment, useMemo } from 'react';
import { Link } from 'react-router';
import {
  ContactInfo,
  useAppointments,
} from '../../../../../data-access/appointments';
import ContactRow from './ContactRow';

const dedupeContacts = (contacts: ContactInfo[]) =>
  contacts.filter(
    (contact, i, arr) =>
      arr.findIndex(
        (c) => c.value === contact.value && c.type === contact.type
      ) === i
  );

type ContactSectionProps = {
  title: string;
  contacts: ContactInfo[];
};

const ContactSection: React.FC<ContactSectionProps> = ({ title, contacts }) =>
  contacts.length ? (
    <FlexBox flexDirection={'column'} gap={2}>
      <Typography variant="subtitle1" color="text.secondary">
        {title}
      </Typography>
      <FlexBox flexDirection={'column'}>
        {contacts.map((contact, i) => (
          <Fragment key={`${contact.type}:${contact.value}`}>
            {i > 0 && <Divider />}
            <ContactRow contact={contact} />
          </Fragment>
        ))}
      </FlexBox>
    </FlexBox>
  ) : null;

const ContactsPage: React.FC = () => {
  const { currentAppointment } = useAppointments();

  const { phones, emails } = useMemo(() => {
    const all = dedupeContacts(currentAppointment?.customer.contacts || []);
    return {
      phones: all.filter((c) => c.type === 'Phone' || c.type === 'MobilePhone'),
      emails: all.filter((c) => c.type === 'Email'),
    };
  }, [currentAppointment?.customer.contacts]);

  const hasAnyContact = !!phones.length || !!emails.length;

  return (
    <PageWithActions
      actionsComponent={
        <FlexBox alignItems={'center'} width={'100%'}>
          <Button component={Link} color={'softGold'} to={'..'}>
            Back
          </Button>
        </FlexBox>
      }
    >
      <FlexBox flexDirection={'column'} gap={4}>
        <Typography variant="h2">
          {currentAppointment?.customer.name
            ? `${currentAppointment.customer.name} contacts`
            : 'Contacts'}
        </Typography>
        {!hasAnyContact ? (
          <EmptyState
            title="No contacts available"
            subtitle="This customer doesn't have any contacts on file."
          />
        ) : (
          <>
            <ContactSection title="Phone numbers" contacts={phones} />
            <ContactSection title="Email addresses" contacts={emails} />
          </>
        )}
      </FlexBox>
    </PageWithActions>
  );
};

export default ContactsPage;
